import React, { useContext } from "react";
import { Container, Headline, Body, Button, Carousel, Hero } from "@web-monorepo/mds";
import SEO from "../components/SEO";
import ImageWithOptimizedVariants from "@src/components/ImageWithOptimizedVariants";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import getLocale from "@src/utils/getLocale";

// Map locales to help center language codes
const HELP_CENTER_LOCALE_MAP: Record<string, string> = {
  "es-mx": "es",
  "es-es": "es",
  "fr-fr": "fr",
  "fr-ca": "fr",
  "pt-br": "pt-br",
  "pt-pt": "pt-br",
  "tr-tr": "tr",
};

const getHelpCenterUrl = (locale: string) => {
  const normalizedLocale = locale.toLowerCase();
  const helpCenterLocale = HELP_CENTER_LOCALE_MAP[normalizedLocale] || "en-us";
  return `https://help.classdojo.com/hc/${helpCenterLocale}/requests/new`;
};

const officeImages = [
  "https://static.classdojo.com/img/2026/01/contact-page/image1.jpg",
  "https://static.classdojo.com/img/2026/01/contact-page/image2.jpg",
  "https://static.classdojo.com/img/2026/01/contact-page/image3.jpg",
  "https://static.classdojo.com/img/2026/01/contact-page/image4.jpg",
  "https://static.classdojo.com/img/2026/01/contact-page/image5.jpg",
];

const FacebookIcon = () => (
  <svg viewBox="0 0 41 41" fill="currentColor" className="size-40">
    <path d="M41 20.5C41 9.178 31.822 0 20.5 0S0 9.178 0 20.5c0 10.23 7.493 18.71 17.281 20.246V26.426h-5.2V20.5h5.2v-4.516c0-5.133 3.057-7.969 7.738-7.969 2.242 0 4.586.4 4.586.4v5.04h-2.583c-2.545 0-3.34 1.58-3.34 3.201V20.5h5.68l-.908 5.926h-4.772v14.32C33.507 39.21 41 30.73 41 20.5z" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 41 41" fill="currentColor" className="size-40">
    <path d="M24.325 17.348L39.5 0h-3.594L22.724 15.064 12.133 0H0l15.918 23.173L0 41.063h3.594l13.918-16.18 11.12 16.18H41L24.324 17.348zm-4.927 5.729l-1.613-2.307L4.88 2.669h5.525l10.358 14.82 1.613 2.307 13.467 19.262h-5.525l-10.92-15.981z" />
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 41 41" fill="currentColor" className="size-40">
    <path d="M20.5 0c-5.567 0-6.266.024-8.453.123-2.183.1-3.673.447-4.978.955a10.05 10.05 0 00-3.634 2.367A10.05 10.05 0 001.068 7.08c-.508 1.305-.855 2.795-.955 4.978C.014 14.244 0 14.943 0 20.51s.024 6.266.123 8.453c.1 2.183.447 3.673.955 4.978a10.05 10.05 0 002.367 3.634 10.05 10.05 0 003.635 2.367c1.305.508 2.795.855 4.978.955 2.187.1 2.886.123 8.453.123s6.266-.024 8.453-.123c2.183-.1 3.673-.447 4.978-.955a10.05 10.05 0 003.634-2.367 10.05 10.05 0 002.367-3.634c.508-1.305.855-2.795.955-4.978.1-2.187.123-2.886.123-8.453s-.024-6.266-.123-8.453c-.1-2.183-.447-3.673-.955-4.978a10.05 10.05 0 00-2.367-3.634A10.05 10.05 0 0033.932 1.08c-1.305-.508-2.795-.855-4.978-.955C26.767.024 26.068 0 20.5 0zm0 3.694c5.474 0 6.122.021 8.284.12 2 .091 3.085.424 3.808.705.957.372 1.64.816 2.358 1.534a6.36 6.36 0 011.534 2.358c.28.723.614 1.808.705 3.808.099 2.162.12 2.81.12 8.284s-.021 6.122-.12 8.284c-.091 2-.424 3.085-.705 3.808a6.36 6.36 0 01-1.534 2.358 6.36 6.36 0 01-2.358 1.534c-.723.28-1.808.614-3.808.705-2.162.099-2.81.12-8.284.12s-6.122-.021-8.284-.12c-2-.091-3.085-.424-3.808-.705a6.36 6.36 0 01-2.358-1.534 6.36 6.36 0 01-1.534-2.358c-.28-.723-.614-1.808-.705-3.808-.099-2.162-.12-2.81-.12-8.284s.021-6.122.12-8.284c.091-2 .424-3.085.705-3.808a6.36 6.36 0 011.534-2.358 6.36 6.36 0 012.358-1.534c.723-.28 1.808-.614 3.808-.705 2.162-.099 2.81-.12 8.284-.12z" />
    <path d="M20.5 27.347a6.847 6.847 0 110-13.694 6.847 6.847 0 010 13.694zm0-17.388c-5.823 0-10.541 4.718-10.541 10.541s4.718 10.541 10.541 10.541 10.541-4.718 10.541-10.541S26.323 9.959 20.5 9.959z" />
    <circle cx="31.377" cy="9.623" r="2.463" />
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 41 41" fill="currentColor" className="size-40">
    <path d="M38.063 0H2.937A2.937 2.937 0 000 2.937v35.126A2.937 2.937 0 002.937 41h35.126A2.937 2.937 0 0041 38.063V2.937A2.937 2.937 0 0038.063 0zM12.172 34.938H6.094V15.375h6.078v19.563zM9.133 12.695a3.523 3.523 0 110-7.046 3.523 3.523 0 010 7.046zm25.805 22.243h-6.075V25.406c0-2.27-.044-5.19-3.162-5.19-3.166 0-3.651 2.473-3.651 5.027v9.695h-6.072V15.375h5.83v2.67h.082c.811-1.538 2.795-3.162 5.753-3.162 6.153 0 7.295 4.05 7.295 9.316v10.739z" />
  </svg>
);

const YouTubeIcon = () => (
  <svg viewBox="0 0 41 29" fill="currentColor" className="size-40">
    <path d="M40.152 4.523A5.147 5.147 0 0036.53.89C33.335 0 20.5 0 20.5 0S7.665 0 4.47.89A5.147 5.147 0 00.848 4.523C0 7.727 0 14.406 0 14.406s0 6.68.848 9.883a5.147 5.147 0 003.622 3.633c3.195.89 16.03.89 16.03.89s12.835 0 16.03-.89a5.147 5.147 0 003.622-3.633C41 21.086 41 14.406 41 14.406s0-6.68-.848-9.883zM16.313 20.5V8.313l10.719 6.093L16.313 20.5z" />
  </svg>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 41 41" fill="currentColor" className="size-40">
    <path
      d="M29.008.334c.388 4.263 2.606 6.823 6.728 7.12v4.584c-2.293.222-4.313-.478-6.638-1.754v7.763c0 9.87-10.728 12.954-15.04 5.882-2.778-4.556-.997-12.55 7.25-12.877v4.834c-.628.103-1.3.265-1.913.491-1.85.683-2.9 1.87-2.63 3.932.52 3.96 7.238 5.138 6.68-2.608V.334h5.563z"
      transform="scale(1.17)"
    />
  </svg>
);

const socialLinks = [
  { name: "Facebook", href: "https://www.facebook.com/classdojo", Icon: FacebookIcon },
  { name: "X", href: "https://twitter.com/classdojo", Icon: XIcon },
  { name: "Instagram", href: "https://www.instagram.com/classdojo", Icon: InstagramIcon },
  { name: "LinkedIn", href: "https://www.linkedin.com/company/classdojo", Icon: LinkedInIcon },
  { name: "YouTube", href: "https://www.youtube.com/classdojo", Icon: YouTubeIcon },
  { name: "TikTok", href: "https://www.tiktok.com/@classdojo", Icon: TikTokIcon },
];

type ContactPageProps = {
  pageContext: { localeStrings: string; currentLocale: string };
};

const ContactPage = ({ pageContext }: ContactPageProps) => {
  const { translate: t } = useContext(TranslationContext);
  const locale = getLocale(pageContext);
  const helpCenterUrl = getHelpCenterUrl(locale);

  return (
    <>
      <SEO
        title={t("contact_page.seo_title") as string}
        description={t("contact_page.seo_description") as string}
        image="https://static.classdojo.com/img/page_contact/contactusFBimage.png"
      />
      <main className="bg-white">
        {/* Hero Section */}
        <Container className="py-2xl">
          <Hero
            layout="split"
            level={1}
            headline={t("contact_page.hero_headline")}
            headlineSize="3"
            body={t("contact_page.hero_body") as string}
            bodySize="2"
          />
        </Container>

        {/* Carousel Section */}
        <section className="pt-0 pb-2xl lg:py-2xl">
          <Carousel>
            {officeImages.map((src) => (
              <Carousel.Item key={src} className="!basis-auto">
                <div className="h-[300px] md:h-[600px]">
                  <ImageWithOptimizedVariants
                    src={src}
                    alt={t("contact_page.carousel_image_alt") as string}
                    className="h-full w-auto rounded-sm"
                  />
                </div>
              </Carousel.Item>
            ))}
          </Carousel>
        </section>

        {/* Address & Social Section */}
        <section className="w-full bg-white pt-lg pb-2xl">
          <Container>
            <div className="mds-grid">
              {/* Address Column */}
              <div className="col-span-full lg:col-span-6 flex flex-col gap-md">
                <Body size="2">{t("contact_page.our_address")}</Body>
                <Headline as="h2" size="4" weight="bold" className="text-neutral-900">
                  2261 Market Street STE 10437
                  <br />
                  San Francisco, CA 94114
                </Headline>
                <a
                  href="https://maps.google.com/?q=2261+Market+Street+STE+10437+San+Francisco+CA+94114"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-primary underline font-bold text-body-2 hover:opacity-80 transition-opacity"
                >
                  {t("contact_page.get_directions")} ↗
                </a>
              </div>

              {/* Social Channels Column */}
              <div className="col-span-full lg:col-span-4 lg:col-start-9 flex flex-col gap-md">
                <Body size="2" className="text-neutral-900">
                  {t("contact_page.social_channels")}
                </Body>
                <div className="flex items-center justify-between">
                  {socialLinks.map(({ name, href, Icon }) => (
                    <a
                      key={name}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-neutral-900 hover:text-brand-primary transition-colors"
                      aria-label={t("contact_page.follow_us_on", { platform: name }) as string}
                    >
                      <Icon />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* Contact, Security & Support Section - 3 Column Layout */}
        <section className="w-full bg-neutral-100 py-2xl">
          <Container>
            <div className="mds-grid">
              {/* Have a Question Column */}
              <div className="col-span-full lg:col-span-4 flex flex-col gap-md mb-xl lg:mb-0">
                <Headline as="h3" size="6" weight="bold" className="text-neutral-900">
                  {t("contact_page.have_a_question_title")}
                </Headline>
                <Body size="1" className="text-neutral-900">
                  {t("contact_page.have_a_question_body")}
                </Body>
                <div className="flex">
                  <Button as="a" href={helpCenterUrl} variant="primary" size="m">
                    {t("contact_page.contact_us_button")}
                  </Button>
                </div>
              </div>

              {/* Security Column */}
              <div className="col-span-full lg:col-span-4 flex flex-col gap-md mb-xl lg:mb-0">
                <Headline as="h3" size="6" weight="bold" className="text-neutral-900">
                  {t("contact_page.security_title")}
                </Headline>
                <Body size="1" className="text-neutral-900">
                  {t("contact_page.security_body")}
                </Body>
                <div className="flex">
                  <Button as="a" href="mailto:security@classdojo.com" variant="primary" size="m">
                    {t("contact_page.ask_security_button")}
                  </Button>
                </div>
              </div>

              {/* Support Column */}
              <div className="col-span-full lg:col-span-4 flex flex-col gap-md">
                <Headline as="h3" size="6" weight="bold" className="text-neutral-900">
                  {t("contact_page.support_title")}
                </Headline>
                <Body size="1" className="text-neutral-900">
                  {t("contact_page.support_body")}
                </Body>
                <div className="flex flex-col gap-sm items-start">
                  <Button
                    as="a"
                    href="https://help.classdojo.com/hc/en-us/categories/200185275"
                    variant="primary"
                    size="m"
                  >
                    {t("contact_page.teacher_help_center")}
                  </Button>
                  <Button
                    as="a"
                    href="https://help.classdojo.com/hc/en-us/categories/200185365"
                    variant="primary"
                    size="m"
                  >
                    {t("contact_page.parent_help_center")}
                  </Button>
                </div>
              </div>
            </div>
          </Container>
        </section>
      </main>
    </>
  );
};

export default ContactPage;
